/**
 * Icon registry (Architecture v2 iconography). The only module that imports lucide-react.
 *
 * - One family: Lucide on a 24-unit grid, round caps and joins, stroke 1.5 (1.75 = `strong`).
 * - Sizes come from tokens: xs 14 · sm 16 (tables, controls) · md 18 (navigation) · lg 20 (empty states).
 * - `directional: true` icons get `mirror-rtl` and flip in right-to-left layouts; nothing else mirrors.
 * - Icons are for navigation, toolbars, repeated concepts in dense metadata and warnings — never decoration.
 * - Four concepts have custom SVGs drawn on the same grid: liability letter, settlement, clawback, bank product config.
 */
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CirclePause,
  CircleX,
  Clock,
  Columns3,
  Copy,
  CornerUpLeft,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  EyeOff,
  FileChartColumn,
  FilePen,
  FileText,
  GitCompareArrows,
  Globe,
  GripVertical,
  Handshake,
  History,
  Hourglass,
  Info,
  Keyboard,
  Landmark,
  Layers,
  LayoutDashboard,
  ListFilter,
  Loader2,
  Lock,
  LockOpen,
  LogOut,
  type LucideIcon,
  ArrowDownToLine,
  BadgeDollarSign,
  Ban,
  CalendarClock,
  ChartColumn,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Coins,
  Contact,
  FileInput,
  FilePenLine,
  FileSpreadsheet,
  FileWarning,
  Files,
  Gauge,
  HandCoins,
  KeyRound,
  Mail,
  Merge,
  Network,
  ShieldCheck,
  Target,
  UserCog,
  UserPlus,
  UserRound,
  UserX,
  WalletCards,
  Workflow,
  FolderKanban,
  Folder,
  FolderPlus,
  FolderInput,
  FileImage,
  File as FileGeneric,
  Presentation,
  Archive,
  Grid3x3,
  FileUp,
  Calculator,
  ArrowLeftRight,
  Waypoints,
  UserCheck,
  ClipboardList,
  FileClock,
  TableProperties,
  BanknoteArrowDown,
  Settings2,
  BadgePercent,
  CircleCheckBig,
  FastForward,
  Radar,
  CircleArrowRight,

  type LucideProps,
  MessageCircleQuestion,
  MessageSquare,
  MessagesSquare,
  MoreHorizontal,
  Package,
  PanelLeft,
  PanelRight,
  Paperclip,
  Pencil,
  PhoneCall,
  Plus,
  ReceiptText,
  RefreshCw,
  Scale,
  ScrollText,
  Search,
  Settings,
  Send,
  Tag,
  Timer,
  Trash2,
  Undo2,
  Upload,
  User,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { forwardRef, type ForwardRefExoticComponent, type RefAttributes, type SVGProps } from 'react';

import { cn } from '../cn';

export type IconProps = Omit<LucideProps, 'ref'>;
type IconBase = ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
export type IconComponent = ReturnType<typeof make>;

/** Icon size tokens (px). Mirrors --icon-size-* in tokens.css. */
export const ICON_SIZE = { xs: 14, sm: 16, md: 18, lg: 20 } as const;
/** Stroke tokens. Mirrors --icon-stroke-* in tokens.css. */
export const ICON_STROKE = { default: 1.5, strong: 1.75 } as const;

function make(Base: LucideIcon | IconBase, directional = false) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon({ className, size = ICON_SIZE.sm, strokeWidth = ICON_STROKE.default, ...props }, ref) {
    return (
      <Base
        ref={ref}
        size={size}
        strokeWidth={strokeWidth}
        aria-hidden={props['aria-label'] ? undefined : true}
        focusable={false}
        className={cn('shrink-0', directional && 'mirror-rtl', className)}
        {...props}
      />
    );
  });
  Icon.displayName = `Icon(${Base.displayName ?? 'custom'})`;
  return Icon;
}

/** Custom glyph on Lucide's 24 grid with the same stroke behaviour. */
function custom(name: string, paths: (p: SVGProps<SVGPathElement>) => React.ReactNode): IconBase {
  const C = forwardRef<SVGSVGElement, IconProps>(function Custom({ size = 24, strokeWidth = 2, color = 'currentColor', absoluteStrokeWidth: _a, ...props }, ref) {
    return (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...props}>
        {paths({})}
      </svg>
    );
  });
  C.displayName = name;
  return C;
}

/** Liability letter: a page with a small pediment (the bank) at its head. */
const LiabilityLetterSvg = custom('LiabilityLetter', () => (
  <>
    <path d="M6 3h8l4 4v14H6z" />
    <path d="M14 3v4h4" />
    <path d="M9 11.5 12 10l3 1.5" />
    <path d="M9.5 12v3M12 12v3M14.5 12v3" />
    <path d="M8.5 17.5h7" />
  </>
));

/** Settlement: two pillars joined by an arrow flowing in reading direction. */
const SettlementSvg = custom('Settlement', () => (
  <>
    <path d="M3 20h5M16 20h5" />
    <path d="M4 20v-9M7 20v-9M17 20v-9M20 20v-9" />
    <path d="M3 9.5 5.5 7 8 9.5M16 9.5 18.5 7 21 9.5" />
    <path d="M9.5 14h5m0 0-2-2m2 2-2 2" />
  </>
));

/** Clawback: a receipt with a return arrow. */
const ClawbackSvg = custom('Clawback', () => (
  <>
    <path d="M5 3v18l2.5-1.5L10 21l2.5-1.5L15 21V3z" />
    <path d="M8 8h4" />
    <path d="M19 11h-6l2-2m-2 2 2 2" />
    <path d="M19 11v5" />
  </>
));

/** Bank product configuration: a landmark with a slider. */
const BankProductConfigSvg = custom('BankProductConfig', () => (
  <>
    <path d="M3 9 11 4l8 5" />
    <path d="M5 10v6M9 10v6M13 10v3" />
    <path d="M3 18h9" />
    <path d="M15 17h6M15 20h6" />
    <circle cx="18.5" cy="17" r="1.5" />
  </>
));

// ---------------------------------------------------------------- registry

interface Entry {
  glyph: LucideIcon | IconBase;
  directional: boolean;
}
const e = (glyph: LucideIcon | IconBase, directional = false): Entry => ({ glyph, directional });

/** Every icon in the product, grouped by purpose. Feature code uses the exports below or <Icon name="…">. */
export const iconRegistry = {
  nav: {
    dashboard: e(LayoutDashboard),
    myWork: e(ClipboardCheck),
    myDeals: e(FolderKanban),
    deals: e(BriefcaseBusiness),
    customers: e(Users),
    operations: e(Workflow),
    banks: e(BankProductConfigSvg),
    employers: e(Building2),
    collections: e(ReceiptText),
    staffCommission: e(HandCoins),
    performance: e(Gauge),
    mis: e(Grid3x3),
    reports: e(FileChartColumn),
    library: e(Files),
    imports: e(FileUp),
    admin: e(UserCog),
    setup: e(Settings),
  },
  concept: {
    deal: e(BriefcaseBusiness),
    customer: e(User),
    bank: e(Landmark),
    product: e(Package),
    scheme: e(Tag),
    employer: e(Building2),
    sales: e(Handshake),
    team: e(Users),
    operations: e(Workflow),
    documents: e(FileText),
    bankQuery: e(MessageCircleQuestion),
    pendingWith: e(Hourglass),
    sla: e(Timer),
    settlement: e(SettlementSvg, true),
    liabilityLetter: e(LiabilityLetterSvg),
    approval: e(CircleCheck),
    rejection: e(CircleX),
    booked: e(BadgeCheck),
    hold: e(CirclePause),
    verificationCall: e(PhoneCall),
    offer: e(FilePen),
    shortfall: e(AlertTriangle),
    commission: e(BadgePercent),
    invoice: e(ReceiptText),
    payment: e(Banknote),
    collection: e(Wallet),
    reconciliation: e(Scale),
    clawback: e(ClawbackSvg),
    report: e(FileChartColumn),
    audit: e(ScrollText),
    configuration: e(Settings2),
    bankProductConfig: e(BankProductConfigSvg),
    version: e(Layers),
    activity: e(Activity),
    // v2 final iteration vocabulary
    customerProfile: e(UserRound),
    files: e(Files),
    externalParty: e(Contact),
    submission: e(Send, true),
    wip: e(Waypoints),
    carryForward: e(CornerUpLeft, true),
    disbursed: e(CircleDollarSign),
    expected: e(Calculator),
    collected: e(BadgeDollarSign),
    outstanding: e(Clock3),
    overdue: e(CalendarClock),
    staffEarnings: e(HandCoins),
    statement: e(TableProperties),
    payout: e(WalletCards),
    adjustment: e(ArrowLeftRight),
    deduction: e(BanknoteArrowDown),
    target: e(Target),
    performance: e(Gauge),
    matrix: e(Grid3x3),
    chart: e(ChartColumn),
    role: e(ShieldCheck),
    scope: e(Radar),
    unit: e(Network),
    user: e(UserCog),
    internalUser: e(UserCheck),
    application: e(ClipboardList),
    financial: e(Coins),
    imports: e(FileUp),
    dueSoon: e(FileClock),
    myDeals: e(FolderKanban),
    completed: e(CircleCheckBig),
    nextAction: e(CircleArrowRight, true),
    bankUpdate: e(MessagesSquare),
    returned: e(Undo2, true),
    imported: e(FileInput),
    expired: e(FileWarning),
    enlistment: e(ClipboardCheck),
    speed: e(FastForward),
    mail: e(Mail),
    folder: e(Folder),
    permissions: e(ShieldCheck),
    calculator: e(Calculator),
  },
  /** Company documents library: the kind of a file, recognised by its type (name column). */
  file: {
    folder: e(Folder),
    pdf: e(FileText),
    image: e(FileImage),
    spreadsheet: e(FileSpreadsheet),
    document: e(FileText),
    presentation: e(Presentation),
    text: e(FileText),
    other: e(FileGeneric),
  },
  /** Deal journey milestones (StageStepper). */
  journey: {
    draft: e(FilePenLine),
    documents: e(Files),
    operations: e(Workflow),
    submitted: e(Send, true),
    bankReview: e(Landmark),
    bankQuery: e(MessageCircleQuestion),
    approved: e(CircleCheck),
    postApproval: e(ClipboardCheck),
    booked: e(BadgeCheck),
    disbursed: e(CircleDollarSign),
    rejected: e(CircleX),
    cancelled: e(Ban),
    hold: e(CirclePause),
  },
  action: {
    search: e(Search),
    filter: e(ListFilter),
    columns: e(Columns3),
    download: e(Download),
    upload: e(Upload),
    add: e(Plus),
    edit: e(Pencil),
    copy: e(Copy),
    open: e(ArrowUpRight, true),
    more: e(Ellipsis),
    refresh: e(RefreshCw),
    history: e(History),
    lock: e(Lock),
    unlock: e(LockOpen),
    compare: e(GitCompareArrows),
    notifications: e(Bell),
    hold: e(CirclePause),
    invite: e(UserPlus),
    deactivate: e(UserX),
    resetSecurity: e(KeyRound),
    merge: e(Merge),
    export: e(FileSpreadsheet),
    import: e(ArrowDownToLine),
    newFolder: e(FolderPlus),
    move: e(FolderInput),
    archive: e(Archive),
    language: e(Globe),
  },
} as const;

type Registry = typeof iconRegistry;
export type IconName = { [G in keyof Registry]: `${G & string}.${keyof Registry[G] & string}` }[keyof Registry];
export type IconTone = 'default' | 'secondary' | 'muted' | 'accent' | 'warning' | 'negative' | 'positive' | 'info';
const TONE: Record<IconTone, string> = {
  default: 'text-ink',
  secondary: 'text-ink-secondary',
  muted: 'text-ink-muted',
  accent: 'text-accent',
  warning: 'text-warning-edge',
  negative: 'text-negative',
  positive: 'text-positive',
  info: 'text-info',
};

/** Every registry entry built once at module load (never during render). */
const ICONS = Object.fromEntries(
  Object.entries(iconRegistry).flatMap(([group, entries]) => Object.entries(entries as Record<string, Entry>).map(([key, entry]) => [`${group}.${key}`, make(entry.glyph, entry.directional)])),
) as Record<IconName, IconComponent>;
const component = (name: IconName): IconComponent => ICONS[name];

/** `<Icon name="concept.bankQuery" size="sm" tone="muted" />` — sizes and tones from tokens. */
export function Icon({ name, size = 'sm', tone, strong, className, ...props }: Omit<IconProps, 'size'> & { name: IconName; size?: keyof typeof ICON_SIZE; tone?: IconTone; strong?: boolean }) {
  const Glyph = ICONS[name];
  return <Glyph size={ICON_SIZE[size]} strokeWidth={strong ? ICON_STROKE.strong : ICON_STROKE.default} className={cn(tone && TONE[tone], className)} {...props} />;
}

// ---------------------------------------------------------------- named exports (existing call sites)

// Navigation
export const DashboardIcon = component('nav.dashboard');
export const InboxIcon = component('nav.myWork');
export const MyWorkIcon = InboxIcon;
export const MyDealsIcon = component('nav.myDeals');
export const ImportsIcon = component('nav.imports');
export const DealsIcon = component('nav.deals');
export const CustomersIcon = component('nav.customers');
export const OperationsIcon = component('nav.operations');
export const CollectionsIcon = component('nav.collections');
export const ReportsIcon = component('nav.reports');
export const BanksIcon = component('nav.banks');
export const EmployersIcon = component('nav.employers');
export const MisIcon = component('nav.mis');
export const StaffCommissionIcon = component('nav.staffCommission');
export const PerformanceIcon = component('nav.performance');
export const AdminIcon = component('nav.admin');
export const LibraryIcon = component('nav.library');
export const SetupIcon = component('nav.setup');

// Concepts
export const CustomerIcon = component('concept.customer');
export const BankIcon = component('concept.bank');
export const EmployerIcon = component('concept.employer');
export const TeamIcon = component('concept.team');
export const PendingWithIcon = component('concept.pendingWith');
export const BankQueryIcon = component('concept.bankQuery');
export const HoldIcon = component('concept.hold');
export const VerificationCallIcon = component('concept.verificationCall');
export const OfferIcon = component('concept.offer');
export const ShortfallIcon = component('concept.shortfall');
export const SettlementIcon = component('concept.settlement');
export const LiabilityLetterIcon = component('concept.liabilityLetter');
export const ClawbackIcon = component('concept.clawback');
export const BankProductConfigIcon = component('concept.bankProductConfig');
export const SchemeIcon = component('concept.scheme');
export const SlaIcon = component('concept.sla');
export const ActivityIcon = component('concept.activity');
export const ApprovalIcon = component('concept.approval');
export const RejectionIcon = component('concept.rejection');
export const BookedIcon = component('concept.booked');

// Actions and toolbars
export const SearchIcon = component('action.search');
export const BellIcon = component('action.notifications');
export const PlusIcon = component('action.add');
export const DownloadIcon = component('action.download');
export const UploadIcon = component('action.upload');
export const FilterIcon = component('action.filter');
export const ColumnsIcon = component('action.columns');
export const MoreIcon = make(MoreHorizontal);
export const CopyIcon = component('action.copy');
export const EditIcon = component('action.edit');
export const DeleteIcon = make(Trash2);
export const RefreshIcon = component('action.refresh');
export const HistoryIcon = component('action.history');
export const OpenIcon = component('action.open');
export const CompareIcon = component('action.compare');
export const SendIcon = make(Send, true);
export const CommentIcon = make(MessageSquare);
export const AttachIcon = make(Paperclip);
export const FileIcon = component('concept.documents');
export const LockIcon = component('action.lock');
export const UnlockIcon = component('action.unlock');
export const EyeIcon = make(Eye);
export const EyeOffIcon = make(EyeOff);
export const LogOutIcon = make(LogOut, true);
export const KeyboardIcon = make(Keyboard);
export const CalendarIcon = make(CalendarDays);
export const DragHandleIcon = make(GripVertical);
export const CloseIcon = make(X);
export const CheckIcon = make(Check);
export const SpinnerIcon = make(Loader2);

// Direction
export const ChevronDownIcon = make(ChevronDown);
export const ChevronUpIcon = make(ChevronUp);
export const ChevronEndIcon = make(ChevronRight, true);
export const ChevronStartIcon = make(ChevronLeft, true);
export const ArrowEndIcon = make(ArrowRight, true);
export const ArrowStartIcon = make(ArrowLeft, true);
export const ArrowUpIcon = make(ArrowUp);
export const ArrowDownIcon = make(ArrowDown);
export const SortIcon = make(ArrowUpDown);
export const ReturnIcon = make(CornerUpLeft, true);
export const UndoIcon = make(Undo2, true);
export const ExternalIcon = make(ExternalLink, true);
export const SidebarToggleIcon = make(PanelLeft, true);
export const ContextToggleIcon = make(PanelRight, true);

// Feedback
export const WarningIcon = make(AlertTriangle);
export const ErrorIcon = make(CircleAlert);
export const InfoIcon = make(Info);
export const SuccessIcon = component('concept.completed');
export const ClockIcon = make(Clock);

/**
 * Soft square icon container (28 or 32 px, 4 px radius). Only for summary figures, key record facts, empty states and
 * high-level business concepts — never beside every label. Tints are white-near; attention never gets a cream fill.
 */
export type IconTileTone = 'default' | 'accent' | 'warning' | 'negative' | 'positive' | 'info';
const TILE_TONE: Record<IconTileTone, string> = {
  default: 'bg-surface-muted text-ink-secondary',
  accent: 'bg-accent-subtle text-accent',
  warning: 'bg-surface-muted text-warning-edge',
  negative: 'bg-negative-subtle text-negative',
  positive: 'bg-positive-subtle text-positive',
  info: 'bg-info-subtle text-info',
};
export function IconTile({ name, size = 'md', tone = 'default', className }: { name: IconName; size?: 'sm' | 'md'; tone?: IconTileTone; className?: string }) {
  return (
    <span aria-hidden="true" className={cn('inline-flex shrink-0 items-center justify-center rounded-sm', size === 'sm' ? 'size-7' : 'size-8', TILE_TONE[tone], className)}>
      <Icon name={name} size={size === 'sm' ? 'sm' : 'md'} />
    </span>
  );
}
